import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { useSubscription } from "./useSubscription";

const FREE_DAILY_LIMIT = 3;

export function useDailyFreeUsage() {
  const { user } = useAuth();
  const { currentPlan } = useSubscription();
  const [todayCount, setTodayCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const isFreeUser = currentPlan?.id === 'free';

  const fetchTodayCount = useCallback(async () => {
    if (!user?.id || !isFreeUser) {
      setTodayCount(0);
      setLoading(false);
      return;
    }

    try {
      const todayStart = new Date();
      todayStart.setHours(0, 0, 0, 0);

      const { count, error } = await supabase
        .from('analysis_history')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .gte('created_at', todayStart.toISOString());

      if (!error) {
        setTodayCount(count || 0);
      }
    } catch (err) {
      console.error('Error fetching daily usage:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.id, isFreeUser]);

  useEffect(() => {
    fetchTodayCount();
  }, [fetchTodayCount]);

  const remaining = Math.max(0, FREE_DAILY_LIMIT - todayCount);
  const isQuotaReached = isFreeUser && remaining <= 0;

  return {
    todayCount,
    remaining,
    dailyLimit: FREE_DAILY_LIMIT,
    isQuotaReached,
    isFreeUser,
    loading,
    refetch: fetchTodayCount,
  };
}
